import { Regimen } from '../../types/Regimen'
import { RecordLabel, PluralRecordLabel } from './types'

/**
 * Maps singular record labels to their plural forms for display
 */
const RECORD_LABEL_PLURALS: Record<RecordLabel, PluralRecordLabel> = {
  'Supplement': 'supplements',
  'Medication': 'medications',
  'Physical Strategy': 'physical strategies',
  'Dental': 'dental work',
  'Mind Strategy': 'mind strategies',
  'Diet': 'diets',
}

/**
 * Gets the plural form of a record label for UI display
 * 
 * @param recordLabel - The singular record label
 * @returns The plural form (e.g., 'Supplement' → 'supplements')
 * 
 * @example
 * ```typescript
 * getPluralLabel('Supplement') // 'supplements'
 * getPluralLabel('Dental') // 'dental work'
 * ```
 */
export function getPluralLabel(recordLabel: RecordLabel): PluralRecordLabel {
  return RECORD_LABEL_PLURALS[recordLabel]
}

/**
 * Determines if a regimen usage is considered "completed"
 * 
 * Completion logic varies by regimen type:
 * 
 * - **Dental procedures**: All dental work is inherently completed (past events).
 *   When hiding completed items, we only show procedures added in the current
 *   reporting month. This check is handled separately via date filtering.
 * 
 * - **Other regimens**: A usage is completed if:
 *   - It's not the most recent usage (usageIndex !== 0), OR
 *   - The entire regimen has an end date
 * 
 * @param usageIndex - Position in the usage history (0 = most recent/current)
 * @param endDate - The regimen's end date (if any)
 * @returns true if the usage should be considered completed
 */
export function isUsageCompleted(
  usageIndex: number,
  endDate: string | undefined
): boolean {
  return usageIndex !== 0 || endDate !== undefined
}

/**
 * Returns a grouping key for a Regimen record.
 *
 * Falls back to the first usage's primary field value when
 * `primaryId` is missing on legacy data.
 */
export function getGroupKey(record: Regimen, firstFieldId: string): string {
  if (record.primaryId) return record.primaryId
  return record.usages[0]?.fields[firstFieldId] ?? record.id
}
